import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { SearchService, SearchResponse } from '@core/services/search.service';
import { CommunityPalette, PostResultVm, getCommunityPalette } from '../health-nav.models';

@Component({
  selector: 'app-post-result-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './post-result-card.component.html',
  styleUrls: ['./post-result-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostResultCardComponent implements OnChanges {
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly cdr = inject(ChangeDetectorRef);
  private metadataRequestId = 0;

  @Input({ required: true }) post!: PostResultVm;

  expanded = false;
  opening = false;
  resolvedCommunityId = '';
  resolvedCommunityName = '';
  resolvedReplyCount?: number;
  resolvedHelpfulReplyCount?: number;
  resolvedPostId = '';

  get communityPalette(): CommunityPalette {
    return getCommunityPalette(
      this.resolvedCommunityId ||
      this.post?.communityId ||
      this.resolvedCommunityName ||
      this.post?.communityName
    );
  }

  get displayCommunityName(): string {
    return this.resolvedCommunityName || this.post.communityName || 'Finding community...';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['post']?.currentValue) return;

    this.resolvedCommunityId = this.post.communityId;
    this.resolvedCommunityName = this.post.communityName;
    this.resolvedReplyCount = this.post.replyCount;
    this.resolvedHelpfulReplyCount = this.post.helpfulReplyCount;
    this.resolvedPostId = this.post.postId;

    void this.hydratePostMetadata();
  }

  async openPost(): Promise<void> {
    if (this.opening) return;

    this.opening = true;

    try {
      const resolvedPostId =
        (await this.resolvePostIdFromSearch()) ??
        (this.looksLikeCanonicalPostId(this.resolvedPostId) ? this.resolvedPostId : null) ??
        (this.looksLikeCanonicalPostId(this.post.postId) ? this.post.postId : null);

      if (!resolvedPostId) return;

      await this.router.navigate(['/post', resolvedPostId]);
    } finally {
      this.opening = false;
    }
  }

  toggleExcerpt(): void {
    this.expanded = !this.expanded;
  }

  private async resolvePostIdFromSearch(): Promise<string | null> {
    if (this.looksLikeCanonicalPostId(this.resolvedPostId)) {
      return this.resolvedPostId;
    }

    try {
      const directMatch = await this.resolvePostIdWithQuery(this.post.title, 10);
      if (directMatch) return directMatch;

      return this.resolvePostIdWithQuery(this.buildFallbackSearchQuery(), 20);
    } catch {
      return null;
    }
  }

  private async hydratePostMetadata(): Promise<void> {
    const requestId = ++this.metadataRequestId;
    const title = this.post.title.trim();
    const fallbackQuery = this.buildFallbackSearchQuery();
    if (!title && !fallbackQuery) return;

    try {
      let matchedPost: any | null = null;

      if (title) {
        const response = await firstValueFrom(this.searchService.search(title, 'posts', 1, 10));
        matchedPost = this.findBestPostCandidate(this.extractPosts(response));
      }

      if (!matchedPost && fallbackQuery) {
        const fallbackPosts = await firstValueFrom(this.searchService.searchPublishedPostsFallback(fallbackQuery, 1, 20));
        matchedPost = this.findBestPostCandidate(fallbackPosts);
      }

      if (!matchedPost || requestId !== this.metadataRequestId) return;

      const resolvedId = matchedPost.id ?? matchedPost.postId;
      const resolvedCommunityName = String(
        matchedPost.categoryName ??
        matchedPost.communityName ??
        matchedPost.category?.name ??
        matchedPost.community?.name ??
        this.post.communityName
      ).trim();
      const resolvedCommunityId = this.resolveCommunityKey(matchedPost, resolvedCommunityName);

      this.resolvedPostId = resolvedId != null ? String(resolvedId).trim() : this.resolvedPostId;
      this.resolvedCommunityName = resolvedCommunityName || this.resolvedCommunityName;
      this.resolvedCommunityId = resolvedCommunityId || this.resolvedCommunityId;
      this.resolvedReplyCount = Number(
        matchedPost.replyCount ?? matchedPost.commentCount ?? matchedPost.commentsCount ?? this.resolvedReplyCount ?? 0
      );
      this.resolvedHelpfulReplyCount = Number(
        matchedPost.helpfulReplyCount ?? matchedPost.helpfulCount ?? matchedPost.helpfulVotes ?? this.resolvedHelpfulReplyCount ?? 0
      );
      this.cdr.markForCheck();
    } catch {
      // Keep parsed values when metadata enrichment fails.
    }
  }

  private extractPosts(response: SearchResponse | null | undefined): any[] {
    if (!response) return [];
    if (Array.isArray(response.posts)) return response.posts as any[];
    if (Array.isArray(response.data?.posts)) return response.data.posts as any[];
    return [];
  }

  private findBestPostMatch(posts: unknown[]): string | null {
    const communityMatch = this.findBestPostCandidate(posts);
    const resolvedId = communityMatch?.id ?? communityMatch?.postId;

    return resolvedId != null ? String(resolvedId).trim() : null;
  }

  private findBestPostCandidate(posts: unknown[]): any | null {
    const normalizedTitle = this.normalizeText(this.post.title);
    const normalizedCommunityName = this.normalizeText(this.post.communityName);

    const candidates = posts
      .map(post => post as any)
      .filter(post => this.isTitleMatch(post, normalizedTitle));

    if (!candidates.length) return null;

    if (!normalizedCommunityName || normalizedCommunityName === 'general') {
      return candidates[0];
    }

    return candidates.find(post => this.matchesCommunity(post, normalizedCommunityName)) ?? candidates[0];
  }

  private async resolvePostIdWithQuery(query: string, pageSize: number): Promise<string | null> {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return null;

    const response = await firstValueFrom(this.searchService.search(normalizedQuery, 'posts', 1, pageSize));
    const directMatch = this.findBestPostMatch(this.extractPosts(response));
    if (directMatch) return directMatch;

    const fallbackPosts = await firstValueFrom(this.searchService.searchPublishedPostsFallback(normalizedQuery, 1, pageSize));
    return this.findBestPostMatch(fallbackPosts);
  }

  private isTitleMatch(post: any, normalizedTitle: string): boolean {
    const candidateTitle = this.normalizeText(post?.title ?? post?.subject ?? '');
    if (!normalizedTitle || !candidateTitle) return false;

    return candidateTitle === normalizedTitle ||
      candidateTitle.includes(normalizedTitle) ||
      normalizedTitle.includes(candidateTitle);
  }

  private buildFallbackSearchQuery(): string {
    return [this.post.title, this.post.excerpt]
      .map(value => String(value ?? '').trim())
      .filter(Boolean)
      .join(' ')
      .slice(0, 180);
  }

  private matchesCommunity(post: any, normalizedCommunityName: string): boolean {
    const candidateCommunity = this.normalizeText(
      post?.categoryName ??
      post?.communityName ??
      post?.category?.name ??
      post?.community?.name ??
      ''
    );

    return Boolean(candidateCommunity) && candidateCommunity === normalizedCommunityName;
  }

  private resolveCommunityKey(post: any, fallbackName: string): string {
    const slug = String(post?.categorySlug ?? post?.communitySlug ?? post?.category?.slug ?? post?.community?.slug ?? '')
      .trim()
      .toLowerCase();

    if (slug) return slug;

    return this.normalizeText(fallbackName).replace(/\s+/g, '-') || this.post.communityId;
  }

  private looksLikeCanonicalPostId(value: string | null | undefined): boolean {
    const normalized = String(value ?? '').trim();
    if (!normalized) return false;

    return /^\d+$/.test(normalized) || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(normalized);
  }

  private normalizeText(value: string | null | undefined): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^\w\s&]+/g, ' ')
      .replace(/\s+/g, ' ');
  }
}
